// Purpose: Allow an invited Event manager to confirm and activate their role.
// Features: Resolves the current Event and authenticated user, invokes the
// trusted manager-invite acceptance callable, shows existing UI states, stores
// the dashboard confirmation flag, and redirects after successful acceptance.
// Dependencies: React, Firebase Auth/Functions, Event context, router navigation,
// browser session storage helpers, and the application logger.
// Important behavior: the browser performs no role or global-user writes;
// authorization and all acceptance updates occur transactionally on the server.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { httpsCallable } from "firebase/functions";

import { auth, functions } from "./firebase";
import { useEventContext } from "./hooks/use_event_context";
import {
  SESSION_KEYS,
  setSessionValue,
} from "./utils/browser_storage_helpers";
import { logger } from "./utils/logger";

const ManagerAccept = () => {
  const [firebaseUser, authLoading] = useAuthState(auth);
  const [status, setStatus] = useState("loading");
  const acceptanceStarted = useRef(false);

  const navigate = useNavigate();

  // ✅ Current event standard
  // ✅ This replaces the old useEvent(eventslug) pattern
  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  useEffect(() => {
    // ✅ Wait until auth and event context are both ready
    if (authLoading || eventLoading || !ready) return;

    // ✅ If not logged in, save the manager-accept intent and redirect to login
    if (!firebaseUser) {
      setSessionValue(SESSION_KEYS.PENDING_MANAGER_ACCEPT, eventslug);
      navigate("/login?ma");
      return;
    }

    // ✅ Defensive guard
    if (!eventId || !eventDoc) return;
    if (acceptanceStarted.current) return;

    acceptanceStarted.current = true;

    let redirectTimer;

    const acceptInvite = async () => {
      try {
        logger.log("🎯 Manager accept started:", {
          eventId,
          eventslug,
          uid: firebaseUser.uid,
        });

        // ✅ The trusted callable validates both pending invite records and
        // performs all role activation writes in one transaction.
        await httpsCallable(functions, "acceptManagerInvite")({ eventId });

        // ✅ Store short-lived confirmation flag for dashboard messaging
        setSessionValue(`managerAccepted_${eventslug}`, "true");

        logger.log("✅ Manager invite accepted:", {
          eventId,
          eventslug,
          uid: firebaseUser.uid,
        });

        setStatus("success");

        // ✅ Redirect after short success display
        redirectTimer = setTimeout(() => {
          navigate("/dashboard?ma");
        }, 4000);
      } catch (err) {
        const errorCode = String(err?.code || "");

        logger.error("❌ Error accepting manager invite:", {
          value: err,
        });

        setStatus(
          errorCode.endsWith("permission-denied") ||
          errorCode.endsWith("failed-precondition")
            ? "unauthorized"
            : "error"
        );
      }
    };

    acceptInvite();

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [
    authLoading,
    eventLoading,
    ready,
    firebaseUser,
    eventId,
    eventDoc,
    eventslug,
    navigate,
  ]);

  if (authLoading || eventLoading || status === "loading") {
    return (
      <div className="container mt-5">
        ⏳ Confirming your manager role...
      </div>
    );
  }

  if (status === "unauthorized") {
    return (
      <div className="container mt-5 text-danger">
        🚫 You are not authorized to accept this manager invite.
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="container mt-5 text-danger">
        ❌ Something went wrong while accepting your invite.
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="container mt-5 text-success">
        <p>
          ✅ You're now the Let's Go Vote manager for{" "}
          <strong>{eventDoc?.name || "this event"}</strong>!
        </p>
        <p>Redirecting to your dashboard...</p>
        <div className="spinner-border text-success mt-3" role="status" />
      </div>
    );
  }

  return null;
};

export default ManagerAccept;
